import Header from './layout/header';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import ClientsCreatedChart from './components/ClientsCreatedChart';
import { _get } from './libraries/fetch';
import { getAllBranchesURL } from './libraries/apiUrl';
import { getBranchLogoUrl, getClientInitials } from './libraries/branchLogo';
import { fetchBranchLicenseDetail, isBranchActiveForClient, normalizeRecordId } from './libraries/licenseExpiry';
import { ensureProductionApiToken, getAdminDesignation, getAdminProfilePicture, getAdminUser } from './libraries/auth';
import {
  canAccessBranchDetails,
  canAccessNavRoute,
  canViewDashboardSection,
  hasModuleAccess,
  isMobileAppBranch,
  MOBILE_CLIENTS_PATH,
  WEB_CLIENTS_PATH,
  resolveClientStatusFilter
} from './libraries/privileges';

function ClientLogoMark({ client, showImage, onLogoError }) {
  if (showImage) {
    return (
      <img
        src={client.logoUrl}
        alt={client.name}
        loading="lazy"
        onError={onLogoError}
        className="client-logos-image"
      />
    );
  }

  return (
    <span className="client-logos-initials">
      {getClientInitials(client.name)}
    </span>
  );
}

function ClientLogoSlot({ client, showImage, onLogoError, onClick, frame = "card" }) {
  const mark = (
    <ClientLogoMark client={client} showImage={showImage} onLogoError={onLogoError} />
  );
  const frameClass = frame === "phone"
    ? "client-logos-slot client-logos-phone"
    : frame === "laptop"
      ? "client-logos-slot client-logos-laptop"
      : "client-logos-slot";

  return (
    <button
      type="button"
      onClick={onClick}
      className={frameClass}
    >
      {frame === "phone" ? (
        <span className="client-logos-phone-bezel">
          <span className="client-logos-phone-notch" />
          <span className="client-logos-phone-screen">
            {mark}
          </span>
          <span className="client-logos-phone-bar" />
        </span>
      ) : frame === "laptop" ? (
        <span className="client-logos-laptop-body">
          <span className="client-logos-laptop-device">
            <span className="client-logos-laptop-lid">
              <span className="client-logos-laptop-screen">
                {mark}
              </span>
            </span>
            <span className="client-logos-laptop-base" />
          </span>
          <span className="client-logos-laptop-name">{client.name}</span>
        </span>
      ) : mark}
      {frame !== "laptop" && (
        <span className="client-logos-tooltip" role="tooltip">
          {client.name}
        </span>
      )}
    </button>
  );
}

// Ignore list as a Set for O(1) lookup
const IGNORE_LIST = new Set([
  "64bf9fe56e1eb79068263fa6",
  "64e5e8f81ca9289ef69a8f3e",
  "64e72bc41ca9289ef6a2ee23",
  "64e84af91ca9289ef6aa8141",
  "64f1c5fc4a74d6538da18e86",
  "651250a01a6bd490a80bb134",
  "665adefe634ebea0973fdb28",
  "6475b8f9ca1bb21ff2fa6913",
  "65d4408ad4d1db5f96081755",
  "65dc317ca2795b43341426f7",
  "6690c8339f9b8cae831b242a",
  "6902f3f64cf88446d8589c25",
  "690300394cf88446d858ef08",
  "690301be4cf88446d858f7f7",
  "6903037c4cf88446d8590436",
  "690304b94cf88446d8590c57",
  "683e8b9a4cf88446d8088b45",
  "67b2faae17701c2c24df4dae",
  "683e78654cf88446d8082575",
  "67879f84165a406d9a846583",
  "66ba093c535ed317419a63a2",
  "68735b604cf88446d8bf2871",
  "68de1d204cf88446d87c5a26",
  "675aae0d12e3fdda6677d2df",
  "6642eab31678eb98b616af02",
  "678f384699156d3a1d3aa6b7",
  "685e43704cf88446d872c023",
]);

// Icon SVGs - defined once outside component
const ICONS = {
  branches: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
  checkmark: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>,
  inactive: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>,
  analytics: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
  settings: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"></circle><path d="M12 1v6m0 6v6m9-9h-6m-6 0H3"></path></svg>,
  time: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#187718" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>,
  arrow: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><polyline points="9 18 15 12 9 6"></polyline></svg>,
  logos: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#187718" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="14" width="7" height="7" rx="1"></rect><rect x="3" y="14" width="7" height="7" rx="1"></rect></svg>,
};

const getClientsFilterPath = (filter, clientsBasePath) => {
  const resolvedFilter = resolveClientStatusFilter(filter);
  return `${clientsBasePath}?filter=${encodeURIComponent(resolvedFilter)}`;
};

const getStatCards = (totalBranches, activeBranches, inactiveBranches, loading, navigate, clientsBasePath) => [
  {
    title: 'Total Clients',
    value: loading ? '...' : totalBranches,
    icon: ICONS.branches,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    onClick: () => navigate(getClientsFilterPath('all', clientsBasePath))
  },
  {
    title: 'Active Clients',
    value: loading ? '...' : activeBranches,
    icon: ICONS.checkmark,
    color: '#4ade80',
    gradient: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
    onClick: () => navigate(getClientsFilterPath('active', clientsBasePath))
  },
  {
    title: 'In-Active Clients',
    value: loading ? '...' : inactiveBranches,
    icon: ICONS.inactive,
    color: '#ef4444',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    onClick: () => navigate(getClientsFilterPath('Expired', clientsBasePath))
  }
];

const getQuickActions = (clientsBasePath, dashboardMode) => [
  {
    title: dashboardMode === 'mobile' ? 'View All Mobile Clients' : 'View All Clients',
    description: dashboardMode === 'mobile'
      ? 'Manage and monitor TecGym Mobile clients'
      : dashboardMode === 'web'
        ? 'Manage and monitor web app gym clients'
        : 'Manage and monitor all gym Clients',
    icon: ICONS.branches,
    color: '#187718',
    path: getClientsFilterPath('all', clientsBasePath),
    opensClientChoice: dashboardMode === 'home'
  },
  {
    title: 'Analytics Dashboard',
    description: dashboardMode === 'mobile'
      ? 'View mobile client analytics and reports'
      : dashboardMode === 'web'
        ? 'View web client analytics and reports'
        : 'View client analytics and reports',
    icon: ICONS.analytics,
    color: '#3b82f6',
    path: dashboardMode === 'home' ? '/web-dashboard' : getClientsFilterPath('all', clientsBasePath),
    clickable: false
  },
  {
    title: 'System Settings',
    description: 'Configure system preferences',
    icon: ICONS.settings,
    color: '#8b5cf6',
    path: '/system-settings'
  }
];

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileDashboard = location.pathname === '/mobile-dashboard';
  const isWebDashboard = location.pathname === '/web-dashboard';
  const dashboardMode = isMobileDashboard ? 'mobile' : isWebDashboard ? 'web' : 'home';
  const clientsBasePath = isMobileDashboard ? MOBILE_CLIENTS_PATH : WEB_CLIENTS_PATH;
  const [totalBranches, setTotalBranches] = useState(0);
  const [activeBranches, setActiveBranches] = useState(0);
  const [inactiveBranches, setInactiveBranches] = useState(0);
  const [clientLogos, setClientLogos] = useState([]);
  const [analyticsSource, setAnalyticsSource] = useState(null);
  const [homeAnalyticsRecords, setHomeAnalyticsRecords] = useState([]);
  const [homeAnalyticsLoading, setHomeAnalyticsLoading] = useState(false);
  const [loopClientLogos, setLoopClientLogos] = useState(false);
  const logoMarqueeRef = useRef(null);
  const logoTrackRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [brokenLogoIds, setBrokenLogoIds] = useState(() => new Set());
  const [signedInPhotoFailed, setSignedInPhotoFailed] = useState(false);
  const [choiceKind, setChoiceKind] = useState(null);
  const canOpenBranchDetails = canAccessBranchDetails();

  useEffect(() => {
    if (!isMobileDashboard && !isWebDashboard) {
      setLoading(false);
      setTotalBranches(0);
      setActiveBranches(0);
      setInactiveBranches(0);
      setClientLogos([]);
      setBrokenLogoIds(new Set());
      return undefined;
    }

    setLoading(true);
    setTotalBranches(0);
    setActiveBranches(0);
    setInactiveBranches(0);
    setClientLogos([]);
    setBrokenLogoIds(new Set());

    let cancelled = false;

    _get(getAllBranchesURL())
      .then(data => {
        if (cancelled) return;

        let branches = [];
        if (Array.isArray(data)) {
          branches = data;
        } else if (data && data.response && Array.isArray(data.response)) {
          branches = data.response;
        } else if (data && data.data && Array.isArray(data.data)) {
          branches = data.data;
        }

        // Normalize id (`id` vs `_id`), filter ignored branches.
        // Mobile dashboard: source is "Mobile App" (or TecGym Mobile). Web dashboard: everyone else.
        branches = branches
          .filter(b => b && (b._id || b.id) && !IGNORE_LIST.has(String(b._id || b.id)))
          .map(b => {
            const id = normalizeRecordId(b._id || b.id);
            const company = normalizeRecordId(b.company);
            return { ...b, _id: id, company: company || b.company };
          })
          .filter((branch) => {
            if (isMobileDashboard) return isMobileAppBranch(branch);
            if (isWebDashboard) return !isMobileAppBranch(branch);
            return true;
          });

        // Show initial counts based on status only
        let activeCount = 0;
        branches.forEach(branch => {
          const status = String(branch.status || '').toLowerCase().trim();
          if (status === 'active') {
            activeCount++;
          }
        });
        setTotalBranches(branches.length);
        setActiveBranches(activeCount);
        setInactiveBranches(branches.length - activeCount);
        setClientLogos(
          branches
            .map((branch) => ({
              id: branch._id,
              name: branch.name || 'Unknown Client',
              logoUrl: getBranchLogoUrl(branch)
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        setLoading(false);

        // Fetch license data to get accurate expired count
        const branchesToCheck = branches.filter(b => b.company && b._id);
        const batchSize = 20;

        const processBatch = async (batch) => {
          const results = await Promise.all(
            batch.map(async (branch) => {
              const branchId = normalizeRecordId(branch._id);
              const licenseData = await fetchBranchLicenseDetail(branch);
              return {
                branchId,
                licenseData: licenseData || null
              };
            })
          );
          return results;
        };

        (async () => {
          await ensureProductionApiToken();
          if (cancelled) return;

          const licenseDataMap = {};
          for (let i = 0; i < branchesToCheck.length; i += batchSize) {
            if (cancelled) return;
            const batch = branchesToCheck.slice(i, i + batchSize);
            const results = await processBatch(batch);
            results.forEach((result) => {
              licenseDataMap[result.branchId] = result.licenseData ?? null;
            });
          }

          if (cancelled) return;

          let finalActiveCount = 0;
          branches.forEach(branch => {
            if (isBranchActiveForClient(branch, licenseDataMap)) {
              finalActiveCount++;
            }
          });
          setTotalBranches(branches.length);
          setActiveBranches(finalActiveCount);
          setInactiveBranches(branches.length - finalActiveCount);
        })();
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isMobileDashboard, isWebDashboard]);

  useEffect(() => {
    if (dashboardMode !== 'home' || !analyticsSource) {
      return undefined;
    }

    let cancelled = false;
    setHomeAnalyticsLoading(true);

    _get(getAllBranchesURL())
      .then((data) => {
        if (cancelled) return;

        let branches = [];
        if (Array.isArray(data)) {
          branches = data;
        } else if (data && Array.isArray(data.response)) {
          branches = data.response;
        } else if (data && Array.isArray(data.data)) {
          branches = data.data;
        }

        const records = branches
          .filter((branch) => branch && (branch._id || branch.id) && !IGNORE_LIST.has(String(branch._id || branch.id)))
          .map((branch) => ({
            ...branch,
            _id: normalizeRecordId(branch._id || branch.id)
          }))
          .filter((branch) => branch._id)
          .filter((branch) => (
            analyticsSource === 'mobile'
              ? isMobileAppBranch(branch)
              : !isMobileAppBranch(branch)
          ))
          .map((branch) => ({
            id: branch._id,
            name: branch.name || 'Unknown Client',
            createdDate: branch.createdDate ?? branch.createdAt ?? branch.created_at ?? null
          }));

        setHomeAnalyticsRecords(records);
        setHomeAnalyticsLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setHomeAnalyticsRecords([]);
          setHomeAnalyticsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dashboardMode, analyticsSource]);

  const statCards = useMemo(
    () => getStatCards(totalBranches, activeBranches, inactiveBranches, loading, navigate, clientsBasePath)
      .filter((card) => {
        // Mobile dashboard: Mobile App module access is enough to show stats.
        // Home / Web dashboard: keep Home privilege checks.
        if (isMobileDashboard && hasModuleAccess('Mobile App')) {
          return true;
        }
        if (card.title === 'Total Clients') return canViewDashboardSection('total');
        if (card.title === 'Active Clients') return canViewDashboardSection('active');
        if (card.title === 'In-Active Clients') return canViewDashboardSection('inactive');
        return true;
      }),
    [totalBranches, activeBranches, inactiveBranches, loading, navigate, clientsBasePath, isMobileDashboard]
  );

  const quickActions = useMemo(
    () => getQuickActions(clientsBasePath, dashboardMode).filter((action) => {
      if (action.opensClientChoice) {
        return canAccessNavRoute('/mobile-dashboard')
          || canAccessNavRoute('/web-dashboard')
          || canAccessNavRoute(MOBILE_CLIENTS_PATH)
          || canAccessNavRoute(WEB_CLIENTS_PATH);
      }
      if (action.clickable === false) {
        return true;
      }
      return canAccessNavRoute(action.path.split('?')[0]);
    }),
    [clientsBasePath, dashboardMode]
  );

  const analyticsChoices = useMemo(() => ([
    canAccessNavRoute('/web-dashboard') && {
      label: 'Web App Clients',
      description: 'Month-wise graph of web clients by the date they were created',
      source: 'web'
    },
    canAccessNavRoute('/mobile-dashboard') && {
      label: 'Mobile App Clients',
      description: 'Month-wise graph of mobile clients by the date they were created',
      source: 'mobile'
    }
  ].filter(Boolean)), []);

  const clientChoices = useMemo(() => {
    const mobilePath = canAccessNavRoute('/mobile-dashboard')
      ? '/mobile-dashboard'
      : canAccessNavRoute(MOBILE_CLIENTS_PATH)
        ? MOBILE_CLIENTS_PATH
        : null;
    const webPath = canAccessNavRoute('/web-dashboard')
      ? '/web-dashboard'
      : canAccessNavRoute(WEB_CLIENTS_PATH)
        ? WEB_CLIENTS_PATH
        : null;

    return [
      webPath && {
        label: 'Web App Clients',
        description: 'Open the TecGym Web App dashboard',
        path: webPath
      },
      mobilePath && {
        label: 'Mobile App Clients',
        description: 'Open the TecGym Mobile dashboard',
        path: mobilePath
      }
    ].filter(Boolean);
  }, []);

  useEffect(() => {
    if (!choiceKind) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setChoiceKind(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [choiceKind]);

  useEffect(() => {
    if (dashboardMode !== 'home' || !analyticsSource || homeAnalyticsLoading) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      document.getElementById('clients-created')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [dashboardMode, analyticsSource, homeAnalyticsLoading]);

  const marqueeLogos = useMemo(
    () => (loopClientLogos && clientLogos.length ? [...clientLogos, ...clientLogos] : clientLogos),
    [clientLogos, loopClientLogos]
  );

  useEffect(() => {
    const marquee = logoMarqueeRef.current;
    const track = logoTrackRef.current;

    if (!marquee || !track || clientLogos.length === 0) {
      setLoopClientLogos(false);
      return undefined;
    }

    const measure = () => {
      const copies = loopClientLogos ? 2 : 1;
      const singleWidth = track.scrollWidth / copies;
      const overflows = singleWidth > marquee.clientWidth + 8;
      setLoopClientLogos((current) => (current === overflows ? current : overflows));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(marquee);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [clientLogos, loopClientLogos, isMobileDashboard, isWebDashboard]);

  const marqueeDuration = useMemo(
    () => Math.max(clientLogos.length * 4, 24),
    [clientLogos.length]
  );

  const handleClientLogoClick = (clientId) => {
    if (canOpenBranchDetails && clientId) {
      const branchIds = clientLogos.map((client) => client.id);
      const currentIndex = branchIds.indexOf(clientId);
      navigate(`/branches/${clientId}`, {
        state: {
          branchId: clientId,
          branchIds,
          currentIndex,
          clientsFilter: 'all',
          clientsSource: isMobileDashboard ? 'mobile' : 'web'
        }
      });
      return;
    }
    navigate(getClientsFilterPath('all', clientsBasePath));
  };

  const handleClientLogoError = (clientId) => {
    setBrokenLogoIds((prev) => {
      if (prev.has(clientId)) return prev;
      const next = new Set(prev);
      next.add(clientId);
      return next;
    });
  };

  const welcomeTitle = isMobileDashboard
    ? 'Welcome To TecGym Mobile Dashboard'
    : isWebDashboard
      ? 'Welcome To TecGym Web App Dashboard'
      : 'Welcome to TecFolk Admin Portal';

  const welcomeSubtitle = isMobileDashboard
    ? 'Manage TecGym Mobile clients with the same overview as Home'
    : isWebDashboard
      ? 'Manage web app clients with the same overview as Home'
      : 'Clients, onboarding, queries, licenses, and staff — the tools in this portal.';

  const isHome = dashboardMode === 'home';
  const signedInUser = isHome ? getAdminUser() : null;
  const signedInLogin = signedInUser?.email || signedInUser?.loginId || '';
  const signedInName = signedInUser?.name || 'Admin';
  const signedInRole = getAdminDesignation(signedInUser);
  const signedInPhoto = getAdminProfilePicture(signedInLogin);

  useEffect(() => {
    setSignedInPhotoFailed(false);
  }, [signedInLogin]);

  return (
    <div className='main'>
      <Header/>
      <div className="page-content" style={{
        background: 'var(--home-page-bg, linear-gradient(135deg, #1e2127 0%, #2a2d35 100%))'
      }}>
        {isHome && (
          <div className="home-user-row">
            <article className="home-user-card">
              <div className="home-user-avatar-wrap">
                {signedInPhoto && !signedInPhotoFailed ? (
                  <img
                    className="home-user-avatar"
                    src={signedInPhoto}
                    alt=""
                    onError={() => setSignedInPhotoFailed(true)}
                  />
                ) : (
                  <span className="home-user-avatar" aria-hidden="true">
                    {(signedInName || signedInLogin || 'A').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="home-user-meta">
                <p className="home-user-status">
                  <span className="home-user-dot" aria-hidden="true" />
                  Signed in
                </p>
                <strong>{signedInName}</strong>
                <div className="home-user-details">
                  {signedInRole ? <span className="home-user-role">{signedInRole}</span> : null}
                  {signedInLogin ? <span className="home-user-login">{signedInLogin}</span> : null}
                </div>
              </div>
            </article>
          </div>
        )}
        {/* Welcome Section */}
        <div className={isHome ? 'home-welcome' : undefined} style={isHome ? undefined : {
          textAlign: 'center',
          marginBottom: 'clamp(32px, 6vw, 60px)'
        }}>
          <h1 style={isHome ? undefined : {
            fontSize: 'clamp(28px, 6vw, 48px)',
            fontWeight: '700',
            margin: '0 0 20px 0',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
            backgroundSize: '300% 300%',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'gradientShift 6s ease infinite',
            letterSpacing: '2px'
          }}>
            {welcomeTitle}
          </h1>
          <p style={isHome ? undefined : {
            fontSize: 'clamp(16px, 3.5vw, 20px)',
            color: 'var(--home-muted, #888)',
            margin: 0,
            fontWeight: '300'
          }}>
            {welcomeSubtitle}
          </p>
        </div>
        {/* Statistics Cards — mobile and web dashboards only */}
        {!isHome && (
        <div className="home-stat-grid">
          {statCards.map((card, index) => (
            <div
              key={index}
              className="stat-card"
              onClick={card.onClick}
              style={{
                backgroundColor: 'var(--home-card-bg, #2a2d35)',
                border: '1px solid var(--home-border, #444)',
                borderRadius: '16px',
                padding: '30px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                transition: 'all 0.3s ease',
                cursor: card.onClick ? 'pointer' : 'default'
              }}
              onMouseEnter={(e) => {
                if (card.onClick) {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)';
                  e.currentTarget.style.borderColor = card.color;
                }
              }}
              onMouseLeave={(e) => {
                if (card.onClick) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3)';
                  e.currentTarget.style.borderColor = 'var(--home-border, #444)';
                }
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: card.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff'
                }}>
                  {card.icon}
                </div>
                <div style={{
                  fontSize: '36px',
                  fontWeight: 'bold',
                  color: card.color
                }}>
                  {card.value}
                </div>
              </div>
              <div style={{
                fontSize: '16px',
                color: 'var(--home-muted, #888)',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                {card.title}
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Client Logos — mobile and web dashboards only */}
        {!isHome && (
        <div className="home-block">
          <h2 className="home-section-title">
            {ICONS.logos}
            Client Logos
          </h2>
          <div style={{
            backgroundColor: 'var(--home-card-bg, #2a2d35)',
            border: '1px solid var(--home-border, #444)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
          }}>
            {loading ? (
              <div style={{ color: 'var(--home-muted, #888)', textAlign: 'center', padding: '24px 0' }}>
                Loading client logos...
              </div>
            ) : clientLogos.length === 0 ? (
              <div style={{ color: 'var(--home-muted, #888)', textAlign: 'center', padding: '24px 0' }}>
                No clients found
              </div>
            ) : (
              <div
                ref={logoMarqueeRef}
                className={loopClientLogos ? "client-logos-marquee" : "client-logos-marquee client-logos-marquee--static"}
                style={{ '--client-logos-duration': `${marqueeDuration}s` }}
              >
                <div
                  ref={logoTrackRef}
                  className={[
                    "client-logos-track",
                    isMobileDashboard ? "client-logos-track--phones" : "",
                    isWebDashboard ? "client-logos-track--laptops" : "",
                    loopClientLogos ? "" : "client-logos-track--static"
                  ].filter(Boolean).join(" ")}
                >
                  {marqueeLogos.map((client, index) => (
                    <ClientLogoSlot
                      key={`${client.id}-${index}`}
                      client={client}
                      frame={isMobileDashboard ? "phone" : isWebDashboard ? "laptop" : "card"}
                      showImage={Boolean(client.logoUrl) && !brokenLogoIds.has(client.id)}
                      onLogoError={() => handleClientLogoError(client.id)}
                      onClick={() => handleClientLogoClick(client.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Quick Actions */}
        <div>
          <h2 className="home-section-title">
            {ICONS.time}
            Quick Actions
          </h2>
          <div className="home-quick-grid">
            {quickActions.map((action, index) => (
              <div
                key={index}
                onClick={() => {
                  if (action.clickable === false) return;
                  if (action.opensClientChoice) {
                    setChoiceKind('clients');
                    return;
                  }
                  navigate(action.path);
                }}
                style={{
                  backgroundColor: 'var(--home-card-bg, #2a2d35)',
                  border: '1px solid var(--home-border, #444)',
                  borderRadius: '12px',
                  padding: '25px',
                  cursor: action.clickable === false ? 'default' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px'
                }}
                onMouseEnter={(e) => {
                  if (action.clickable === false) return;
                  e.currentTarget.style.backgroundColor = 'var(--home-card-hover, #33363f)';
                  e.currentTarget.style.borderColor = action.color;
                  e.currentTarget.style.transform = 'translateX(5px)';
                  e.currentTarget.style.boxShadow = `0 4px 16px ${action.color}40`;
                }}
                onMouseLeave={(e) => {
                  if (action.clickable === false) return;
                  e.currentTarget.style.backgroundColor = 'var(--home-card-bg, #2a2d35)';
                  e.currentTarget.style.borderColor = 'var(--home-border, #444)';
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '10px',
                  backgroundColor: `${action.color}20`,
                  color: action.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {action.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: '0 0 8px 0',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'var(--home-heading, #fff)'
                  }}>
                    {action.title}
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    color: 'var(--home-muted, #888)',
                    lineHeight: '1.5'
                  }}>
                    {action.description}
                  </p>
                </div>
                {action.clickable !== false && (
                  <div style={{ flexShrink: 0, marginTop: '5px' }}>
                    {ICONS.arrow}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {isHome && analyticsSource && (
          <ClientsCreatedChart
            clients={homeAnalyticsRecords}
            sourceLabel={analyticsSource === 'mobile' ? 'Mobile App' : 'Web App'}
            loading={homeAnalyticsLoading}
          />
        )}

      </div>

      {choiceKind && (
        <div
          className="client-choice-backdrop"
          onClick={() => setChoiceKind(null)}
        >
          <div
            className="client-choice-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="client-choice-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="client-choice-header">
              <h2 id="client-choice-title">
                {choiceKind === 'analytics' ? 'Analytics Dashboard' : 'View All Clients'}
              </h2>
              <button
                type="button"
                className="client-choice-close"
                aria-label="Close"
                onClick={() => setChoiceKind(null)}
              >
                ×
              </button>
            </div>
            <p>
              {choiceKind === 'analytics'
                ? 'Choose web or mobile. The graph shows how many clients were created in each month.'
                : 'Choose which clients dashboard to open.'}
            </p>
            <div className="client-choice-options">
              {(choiceKind === 'analytics' ? analyticsChoices : clientChoices).map((choice) => (
                <button
                  key={choice.path || choice.source}
                  type="button"
                  className="client-choice-option"
                  onClick={() => {
                    setChoiceKind(null);
                    if (choice.source) {
                      setAnalyticsSource(choice.source);
                      return;
                    }
                    navigate(choice.path);
                  }}
                >
                  <span>
                    <strong>{choice.label}</strong>
                    <small>{choice.description}</small>
                  </span>
                  {ICONS.arrow}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .stat-card {
          animation: fadeIn 0.6s ease-out;
        }
        .client-logos-marquee {
          overflow: hidden;
          width: 100%;
          container-type: inline-size;
        }
        .client-logos-track {
          display: flex;
          width: max-content;
          gap: 10px;
          animation: clientLogosScroll var(--client-logos-duration, 40s) linear infinite;
        }
        .client-logos-marquee:hover .client-logos-track {
          animation-play-state: paused;
        }
        .client-logos-marquee--static {
          display: flex;
          justify-content: center;
        }
        .client-logos-track--static {
          animation: none;
        }
        .client-logos-slot {
          flex: 0 0 10cqi;
          width: 10cqi;
          height: 10cqi;
          box-sizing: border-box;
          padding: 0;
          margin: 0;
          background-color: #fff;
          border: 1px solid var(--home-border, #444);
          border-radius: 12px;
          cursor: pointer;
          display: block;
          overflow: hidden;
          position: relative;
          transition: border-color 0.2s ease, transform 0.2s ease;
        }
        .client-logos-slot:hover {
          border-color: #187718;
          transform: translateY(-2px);
          z-index: 2;
        }
        .client-logos-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
          padding: 6px;
          box-sizing: border-box;
        }
        .client-logos-initials {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(14px, 4cqi, 22px);
          font-weight: 700;
          color: #187718;
          background-color: #fff;
        }
        .client-logos-tooltip {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          padding: 8px 6px;
          background: rgba(24, 119, 24, 0.95);
          color: #fff;
          font-size: clamp(12px, 3.5cqi, 18px);
          font-weight: 600;
          line-height: 1.3;
          text-align: center;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-100%);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
          pointer-events: none;
          word-break: break-word;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          z-index: 1;
        }
        .client-logos-slot:hover .client-logos-tooltip {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        .client-logos-track--phones {
          align-items: flex-end;
          gap: 18px;
        }
        .client-logos-phone {
          flex: 0 0 7.5cqi;
          width: 7.5cqi;
          height: 15.2cqi;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
        }
        .client-logos-phone:hover {
          border-color: transparent;
          transform: translateY(-4px);
        }
        .client-logos-phone-bezel {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          height: 100%;
          box-sizing: border-box;
          background: #12141a;
          border: 2px solid #3d4350;
          border-radius: 22%;
          padding: 8% 7% 6%;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.28);
        }
        .client-logos-phone:hover .client-logos-phone-bezel {
          border-color: #187718;
        }
        .client-logos-phone-notch {
          width: 34%;
          height: 4.5%;
          min-height: 4px;
          flex-shrink: 0;
          background: #07080b;
          border-radius: 999px;
          margin-bottom: 7%;
        }
        .client-logos-phone-screen {
          flex: 1;
          width: 100%;
          min-height: 0;
          background: #fff;
          border-radius: 12%;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .client-logos-phone-bar {
          width: 28%;
          height: 3.2%;
          min-height: 3px;
          flex-shrink: 0;
          background: #2a2f3a;
          border-radius: 999px;
          margin-top: 6%;
        }
        .client-logos-phone .client-logos-image,
        .client-logos-phone .client-logos-initials {
          width: 100%;
          height: 100%;
        }
        .client-logos-phone .client-logos-tooltip {
          top: auto;
          bottom: 100%;
          border-radius: 8px;
          margin-bottom: 6px;
          transform: translateY(6px);
        }
        .client-logos-phone:hover .client-logos-tooltip {
          transform: translateY(0);
        }
        .client-logos-track--laptops {
          align-items: flex-end;
          gap: 18px;
        }
        .client-logos-laptop {
          flex: 0 0 13cqi;
          width: 13cqi;
          height: 13.4cqi;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
        }
        .client-logos-laptop:hover {
          border-color: transparent;
          transform: translateY(-4px);
        }
        .client-logos-laptop-body {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          height: 100%;
        }
        .client-logos-laptop-device {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          height: 10.2cqi;
          flex-shrink: 0;
        }
        .client-logos-laptop-name {
          display: -webkit-box;
          width: 100%;
          margin-top: 6px;
          color: var(--home-heading, #fff);
          font-size: clamp(11px, 1.5cqi, 14px);
          font-weight: 600;
          line-height: 1.25;
          text-align: center;
          word-break: break-word;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .client-logos-laptop-lid {
          flex: 1;
          width: 88%;
          min-height: 0;
          box-sizing: border-box;
          display: flex;
          background: #12141a;
          border: 2px solid #3d4350;
          border-bottom: none;
          border-radius: 8% 8% 1.5% 1.5%;
          padding: 5.5% 4.5% 3.5%;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.22);
        }
        .client-logos-laptop-screen {
          flex: 1;
          min-height: 0;
          background: #fff;
          border-radius: 3%;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .client-logos-laptop-base {
          width: 100%;
          height: 16%;
          flex-shrink: 0;
          position: relative;
          background: linear-gradient(180deg, #3a4150 0%, #1c2028 100%);
          border: 2px solid #3d4350;
          border-radius: 2% 2% 18% 18% / 8% 8% 70% 70%;
          box-shadow: 0 8px 14px rgba(0, 0, 0, 0.28);
        }
        .client-logos-laptop-base::before {
          content: "";
          position: absolute;
          left: 50%;
          top: 0;
          width: 16%;
          height: 42%;
          transform: translateX(-50%);
          background: #12141a;
          border-radius: 0 0 40% 40%;
        }
        .client-logos-laptop:hover .client-logos-laptop-lid,
        .client-logos-laptop:hover .client-logos-laptop-base {
          border-color: #187718;
        }
        .client-logos-laptop .client-logos-image,
        .client-logos-laptop .client-logos-initials {
          width: 100%;
          height: 100%;
        }
        @keyframes clientLogosScroll {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </div>
  );
}

export default App;
